import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type ReconciliationStatus = 'MATCH' | 'MISMATCH' | 'PENDING' | 'UNKNOWN' | 'LIMITED';

type Primitive = string | number | boolean | null;
type Projection = Record<string, Primitive | undefined>;

export interface FinancialReconciliation {
  order_id: string;
  status: ReconciliationStatus;
  observed_at: string;
  revisions: {
    order_aggregate_version: number;
    order_row_version: number;
    payments: { payment_id: string; attempt_id: string; version: number }[];
    payment_sources: { event_id: string; source_version: number | null }[];
  };
  order: {
    status: string;
    current_attempt_id: string | null;
    amount_minor: number;
    currency: string;
    payment_approved: boolean;
    refunded_minor: number;
  };
  payments: Projection[];
  sources: Projection[];
  refund_operations: Projection[];
  refund_requests: Projection[];
  receipts: Projection[];
  findings: string[];
  judgment_scope: string[];
}

const knownStatus = new Set<ReconciliationStatus>([
  'MATCH',
  'MISMATCH',
  'PENDING',
  'UNKNOWN',
  'LIMITED',
]);
const currency = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Z]{3}$/.test(value);
const amount = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const nonnegativeInteger = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

function invalid(): ApiError {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '금융 정보를 확인하지 못했습니다. 관리자에게 문의해 주세요.',
  });
}

function requiredRecord(value: unknown, keys: readonly string[]): Record<string, unknown> {
  if (!record(value) || keys.some((key) => !(key in value))) throw invalid();
  return value;
}

type ProjectionKind =
  | 'text'
  | 'uuid'
  | 'integer'
  | 'amount'
  | 'currency'
  | 'date'
  | 'boolean'
  | 'nullable-text'
  | 'nullable-uuid'
  | 'nullable-integer'
  | 'nullable-amount'
  | 'nullable-currency'
  | 'nullable-date'
  | 'nullable-boolean';

function validProjectionValue(value: unknown, kind: ProjectionKind): boolean {
  if (kind.startsWith('nullable-') && value === null) return true;
  switch (kind.replace(/^nullable-/, '')) {
    case 'text':
      return text(value);
    case 'uuid':
      return isUuid(value);
    case 'integer':
      return Number.isSafeInteger(value) && Number(value) >= 0;
    case 'amount':
      return amount(value);
    case 'currency':
      return currency(value);
    case 'date':
      return isOffsetDateTime(value);
    case 'boolean':
      return typeof value === 'boolean';
  }
  return false;
}

function readProjection(
  value: unknown,
  schema: Readonly<Record<string, ProjectionKind>>,
): Projection {
  if (!record(value)) throw invalid();
  const result: Projection = {};
  for (const [key, kind] of Object.entries(schema)) {
    if (!(key in value)) continue;
    const item = value[key];
    if (!validProjectionValue(item, kind)) throw invalid();
    result[key] = item as Primitive;
  }
  return result;
}

function readReconciliation(value: unknown, requestedOrderId: string): FinancialReconciliation {
  const data = requiredRecord(value, [
    'order_id',
    'status',
    'observed_at',
    'revisions',
    'order',
    'payments',
    'sources',
    'refund_operations',
    'refund_requests',
    'receipts',
    'findings',
    'judgment_scope',
  ]);
  if (
    data.order_id !== requestedOrderId ||
    typeof data.status !== 'string' ||
    !knownStatus.has(data.status as ReconciliationStatus) ||
    !isOffsetDateTime(data.observed_at) ||
    !Array.isArray(data.payments) ||
    !Array.isArray(data.sources) ||
    !Array.isArray(data.refund_operations) ||
    !Array.isArray(data.refund_requests) ||
    !Array.isArray(data.receipts) ||
    !Array.isArray(data.findings) ||
    !data.findings.every(text) ||
    !Array.isArray(data.judgment_scope) ||
    !data.judgment_scope.every(text)
  )
    throw invalid();
  const revisions = requiredRecord(data.revisions, [
    'order_aggregate_version',
    'order_row_version',
    'payments',
    'payment_sources',
  ]);
  if (
    !nonnegativeInteger(revisions.order_aggregate_version) ||
    !nonnegativeInteger(revisions.order_row_version) ||
    !Array.isArray(revisions.payments) ||
    !Array.isArray(revisions.payment_sources)
  )
    throw invalid();
  const revisionPayments = revisions.payments.map((item) => {
    const row = requiredRecord(item, ['payment_id', 'attempt_id', 'version']);
    if (!isUuid(row.payment_id) || !isUuid(row.attempt_id) || !nonnegativeInteger(row.version))
      throw invalid();
    return { payment_id: row.payment_id, attempt_id: row.attempt_id, version: row.version };
  });
  const revisionSources = revisions.payment_sources.map((item) => {
    const row = requiredRecord(item, ['event_id', 'source_version']);
    if (
      !isUuid(row.event_id) ||
      !(row.source_version === null || nonnegativeInteger(row.source_version))
    )
      throw invalid();
    return { event_id: row.event_id, source_version: row.source_version };
  });
  const order = requiredRecord(data.order, [
    'status',
    'current_attempt_id',
    'amount_minor',
    'currency',
    'payment_approved',
    'refunded_minor',
  ]);
  if (
    !text(order.status) ||
    !(order.current_attempt_id === null || isUuid(order.current_attempt_id)) ||
    !amount(order.amount_minor) ||
    !currency(order.currency) ||
    typeof order.payment_approved !== 'boolean' ||
    !amount(order.refunded_minor)
  )
    throw invalid();

  const projections: Record<string, Readonly<Record<string, ProjectionKind>>> = {
    payments: {
      payment_id: 'uuid',
      attempt_id: 'uuid',
      version: 'integer',
      status: 'text',
      amount_minor: 'nullable-amount',
      currency: 'nullable-currency',
    },
    sources: {
      event_id: 'nullable-uuid',
      payment_id: 'nullable-uuid',
      attempt_id: 'nullable-uuid',
      attempt_scope: 'text',
      event_type: 'text',
      source_version: 'nullable-integer',
      delivery_status: 'text',
      amount_minor: 'nullable-amount',
      currency: 'nullable-currency',
      operation_id: 'nullable-uuid',
      payment_status: 'nullable-text',
    },
    refund_operations: {
      operation_id: 'nullable-uuid',
      payment_id: 'nullable-uuid',
      attempt_id: 'nullable-uuid',
      sequence: 'integer',
      amount_minor: 'amount',
      currency: 'currency',
      classification: 'text',
      status: 'text',
      cancel_kind: 'nullable-text',
      created_at: 'date',
      updated_at: 'date',
    },
    refund_requests: {
      request_operation_id: 'uuid',
      sequence: 'integer',
      amount_minor: 'amount',
      completed_minor: 'amount',
      currency: 'currency',
      status: 'text',
      capture_wait_recorded: 'boolean',
      created_at: 'date',
      updated_at: 'date',
    },
    receipts: {
      event_id: 'nullable-uuid',
      operation_id: 'nullable-uuid',
      fact_kind: 'nullable-text',
    },
  };
  const mapped = Object.fromEntries(
    Object.entries(projections).map(([key, schema]) => [
      key,
      (data[key] as unknown[]).map((item) => readProjection(item, schema)),
    ]),
  ) as Pick<
    FinancialReconciliation,
    'payments' | 'sources' | 'refund_operations' | 'refund_requests' | 'receipts'
  >;
  return {
    order_id: data.order_id,
    status: data.status as ReconciliationStatus,
    observed_at: data.observed_at,
    revisions: {
      order_aggregate_version: revisions.order_aggregate_version,
      order_row_version: revisions.order_row_version,
      payments: revisionPayments,
      payment_sources: revisionSources,
    },
    order: {
      status: order.status,
      current_attempt_id: order.current_attempt_id,
      amount_minor: order.amount_minor,
      currency: order.currency,
      payment_approved: order.payment_approved,
      refunded_minor: order.refunded_minor,
    },
    ...mapped,
    findings: data.findings,
    judgment_scope: data.judgment_scope,
  };
}

export async function readFinancialReconciliation(
  client: ServiceClient,
  orderId: string,
  signal?: AbortSignal,
): Promise<FinancialReconciliation> {
  if (!isUuid(orderId)) throw invalid();
  const data = await client.get<unknown>(`admin/orders/${orderId}/financial-reconciliation`, {
    signal,
  });
  return readReconciliation(data, orderId);
}
